// src/components/InvoiceProcessor.tsx
import React, { useState } from 'react';
import { Upload, Progress, Button, Divider, Modal } from 'antd';
import { InboxOutlined, FileTextOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { storage, db } from '../firebaseConfig';
import imageCompression from 'browser-image-compression';
import Lottie from 'lottie-react';
import loadingAnimation from './lottie/isProcessing.json';
import errorAnimation from './lottie/error.json';
import CameraCapture from './CameraCapture';
import { v4 as uuidv4 } from 'uuid';
import './InvoiceProcessor.css';

const { Dragger } = Upload;

const InvoiceProcessor = ({ onProcessComplete }) => {
  /* ---------------------- состояние UI ------------------------------------ */
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [isCriteriaExpanded, setIsCriteriaExpanded] = useState(false);

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error('Błąd podczas kompresji obrazu:', error);
      return file;
    }
  };

  // ――― dataURLtoFile (нужна при confirmScan) ―――
  const dataURLtoFile = (dataurl, filename) => {
    const [meta, base64] = dataurl.split(',');
    const mime = meta.match(/:(.*?);/)[1];
    const bytes = atob(base64);
    const buf = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
    return new File([buf], filename, { type: mime });
  };

  // ――― ограничение понедельник 9-15 ―――
  const now = new Date();
  const isMonday9to15 =
    now.getDay() === 1 && now.getHours() >= 9 && now.getHours() < 15;

  const handleFileUpload = async (files) => {
    setLoading(true);
    setIsProcessing(true);
    setIsUploading(true);
    setHasError(false);
    setMessageContent('');
    setUploadProgress(0);

    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        let processedFile = file;

        // Compress file if >10MB
        if (file.size > 10 * 1024 * 1024) {
          setMessageContent(`Kompresowanie obrazu ${file.name}...`);
          try {
            processedFile = await compressImage(file);
          } catch (error) {
            console.error(`Error compressing image ${file.name}:`, error);
            setMessageContent(`Nie udało się skompresować obrazu ${file.name}. Przesyłamy oryginał.`);
          }
        }

        const uniqueFilename = `invoices/${uuidv4()}_${file.name}`;
        const uniqueFile = new File([processedFile], uniqueFilename, { type: file.type });

        setMessageContent(`Przesyłanie ${file.name}...`);
        const storageRef = ref(storage, uniqueFilename);
        const uploadTask = uploadBytesResumable(storageRef, uniqueFile);

        return new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(Math.round(progress));
              setMessageContent(`Przesyłanie ${file.name}: ${Math.round(progress)}%`);
            },
            (error) => {
              console.error('Firebase upload error:', error);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

                // Сохраняем в Firestore с правильным полем fileURL
                const docData = {
                  fileURL: downloadURL, // ✅ Изменено с url на fileURL
                  timestamp: serverTimestamp(),
                  status: "W trakcie weryfikacji",
                  filename: file.name
                };

                const docRef = await addDoc(collection(db, 'invoices'), docData);
                console.log('Document written with ID: ', docRef.id);

                // Вызываем callback если нужно
                if (onProcessComplete) {
                  onProcessComplete({
                    id: docRef.id,
                    fileURL: downloadURL, // ✅ Изменено с url на fileURL
                    status: "W trakcie weryfikacji",
                    filename: file.name
                  });
                }

                resolve(docRef.id);
              } catch (error) {
                console.error('Error saving to Firestore:', error);
                reject(error);
              }
            }
          );
        });
      });

      // Ждем загрузки всех файлов
      await Promise.all(uploadPromises);

      // Показываем сообщение об успехе
      setMessageContent(
        files.length === 1
          ? `Faktura została pomyślnie przesłana i oczekuje na weryfikację!`
          : `Wszystkie ${files.length} faktury zostały pomyślnie przesłane!`
      );

      // Автоматически возвращаемся к форме через 3 секунды
      setTimeout(() => {
        setIsProcessing(false);
        setIsUploading(false);
        setUploadProgress(0);
        setMessageContent('');
        setLoading(false);
        setHasError(false);
      }, 3000);

    } catch (error) {
      console.error('Upload error:', error);
      setMessageContent(`Błąd przesyłania: ${error.message}`);
      setHasError(true);
      setIsProcessing(false);
      setIsUploading(false);
      setLoading(false);
    }
  };

  const startScanning = () => {
    setIsModalVisible(true);
    setIsScanning(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setIsScanning(false);
    setCapturedImage(null);
  };

  const retry = () => {
    setHasError(false);
    setIsProcessing(false);
    setIsUploading(false);
    setMessageContent('');
  };

  const retakeScan = () => {
    setCapturedImage(null);
    setIsScanning(true);
  };

  const confirmScan = () => {
    const uniqueId = uuidv4();
    const uniqueFilename = `captured_image_${uniqueId}.jpg`;
    const file = dataURLtoFile(capturedImage, uniqueFilename);
    handleFileUpload([file]);
    setIsScanning(false);
    setIsModalVisible(false);
  };

  const draggerProps = {
    name: 'file',
    multiple: true,
    beforeUpload: (file, fileList) => {
      handleFileUpload(fileList);
      return false;
    },
    onDrop(e) {
      console.log('Upuszczone pliki', e.dataTransfer.files);
    },
  };

  if (isMonday9to15) {
    return (
      <div className="invoice-processor">
        <div className="maintenance-notice">
          <div className="maintenance-icon">
            <FileTextOutlined />
          </div>
          <h2>Dodawanie faktur tymczasowo niedostępne</h2>
          <div className="maintenance-content">
            <p><strong>Drodzy Kierowcy,</strong></p>
            <p>W każdy poniedziałek w godzinach <span className="highlight">9:00 – 15:00</span> przeprowadzamy rozliczenie poprzedniego tygodnia.</p>
            <p>W tym czasie nie można dodawać nowych faktur.</p>
            <div className="maintenance-cta">
              <CheckCircleOutlined />
              <span>Zapraszamy do dodawania faktur po godzinie 15:00</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-processor">
      {!isProcessing && !isUploading && !hasError && (
        <>
          <div className="upload-section">
            <Dragger {...draggerProps} className="custom-dragger">
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Kliknij lub przeciągnij plik, aby go przesłać</p>
              <p className="ant-upload-hint">
                Obsługa wielu plików. Surowo zabroniona jest przesyłka danych poufnych.
              </p>
              {loading && <Progress percent={uploadProgress} className="upload-progress" />}
            </Dragger>
          </div>
              <div className="company-info">
                <strong>Dane firmy:</strong> SPARIO SP. Z O.O., <br />UL. SZLAK 77 / 222,<br />31-153 KRAKÓW, <br />NIP 6762679855
              </div>
          <div className="criteria-section">
            <div 
              className={`criteria-header ${isCriteriaExpanded ? 'active' : ''}`}
              onClick={() => setIsCriteriaExpanded(!isCriteriaExpanded)}
            >
              <div className="criteria-title">
                <FileTextOutlined /> 
                Kryteria weryfikacji faktury
              </div>
              <div className={`criteria-chevron ${isCriteriaExpanded ? 'active' : ''}`}>
                ▼
              </div>
            </div>
            
            <div className={`criteria-content ${!isCriteriaExpanded ? 'collapsed' : ''}`}>
              <div className="criteria-grid">
                <div className="criteria-item">
                  <span className="criteria-number">1</span>
                  <span>Poprawny numer rejestracyjny</span>
                </div>
                <div className="criteria-item">
                  <span className="criteria-number">2</span>
                  <span>Data zakupu</span>
                </div>
                <div className="criteria-item">
                  <span className="criteria-number">3</span>
                  <span>Prawidłowy adres nabywcy</span>
                </div>
                <div className="criteria-item">
                  <span className="criteria-number">4</span>
                  <span>Czytelna faktura</span>
                </div>
                <div className="criteria-item">
                  <span className="criteria-number">5</span>
                  <span>Dane paliwa (litry, rodzaj, cena)</span>
                </div>
                <div className="criteria-item">
                  <span className="criteria-number">6</span>
                  <span>Poprawne wskazanie VAT</span>
                </div>
              </div>
            
              
              <div className="deadline-info">
                <strong>Termin:</strong> Faktury można dodawać za dany tydzień do poniedziałku do godziny 8:00
              </div>
              
              <div className="warning-note">
                ⚠️ Faktury, które nie spełniają podanych kryteriów, zostaną odrzucone.
              </div>
            </div>
          </div>
        </>
      )}

      {(isProcessing || isUploading || hasError) && (
        <div className="processing-section">
          <div className="processing-animation">
            <Lottie
              animationData={hasError ? errorAnimation : loadingAnimation}
              loop={!hasError}
              className="lottie-animation"
            />
          </div>
          <div className="processing-progress">
            <Progress
              percent={Math.round(uploadProgress)}
              strokeColor="#1890ff"
              status={hasError ? 'exception' : 'active'}
              className="custom-progress"
            />
            <p className="processing-message">
              {messageContent.length > 50 ? messageContent.slice(0, 50) + '...' : messageContent}
            </p>
          </div>
          {hasError && (
            <Button type="primary" onClick={retry} className="retry-button">
              Spróbuj ponownie
            </Button>
          )}
        </div>
      )}

      <Modal
        title="Skanowanie dokumentu"
        visible={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width="90%"
        style={{ maxWidth: '600px', top: 20 }}
        className="scanning-modal"
        centered
      >
        {isProcessing ? (
          <div className="modal-processing">
            <Lottie animationData={loadingAnimation} loop={true} className="modal-lottie" />
          </div>
        ) : capturedImage ? (
          <div className="captured-image-section">
            <img
              src={capturedImage}
              alt="Zeskanowany dokument"
              className="captured-image"
            />
            <div className="modal-actions">
              <Button type="default" onClick={retakeScan} className="modal-button">
                Skanuj ponownie
              </Button>
              <Button type="primary" onClick={confirmScan} className="modal-button">
                Dalej
              </Button>
            </div>
          </div>
        ) : (
          <div className="camera-section">
            <CameraCapture
              isScanning={isScanning}
              onCapture={(imageSrc) => setCapturedImage(imageSrc)}
              className="camera-capture"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InvoiceProcessor;